import {
  FormField,
  type IFormFieldProps,
  type IMultiSelectProps,
  Select,
  type ISelectOption,
  type ISingleSelectProps,
} from "@/ui-kit";
import { useCallback } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import { get } from "../utils";
import { useTranslation } from "react-i18next";

type ISelectOmitProps = "label" | "value" | "onChange";

type BaseSelectControlProps = Partial<IFormFieldProps> & {
  name: string;
  formElementClass?: string;
  clear?: boolean;
};

export type SingleSelectControlProps = Omit<
  ISingleSelectProps,
  ISelectOmitProps
> &
  BaseSelectControlProps & {
    onChange?: (value: string | undefined, item?: ISelectOption) => void;
  };

export type MultiSelectControlProps = Omit<
  IMultiSelectProps,
  ISelectOmitProps
> &
  BaseSelectControlProps & {
    onChange?: (value: string[], item?: ISelectOption) => void;
  };

type Props = SingleSelectControlProps | MultiSelectControlProps;

export const SelectControl = (props: Props) => {
  const {
    label,
    message,
    required,
    tooltipContent,
    accessory,
    formElementClass,
    name,
    invalid,
    onChange,
    clear,
    description,
    ...restSelectProps
  } = props;
  const { t } = useTranslation();
  const { control } = useFormContext();
  const { errors } = useFormState();
  const errorMessage = get(errors, name)?.message as string | undefined;

  const placeholder =
    props.placeholder !== undefined
      ? props.placeholder
      : label && typeof label === "string"
        ? t("placeholder.enter_what", {
            what: label.toLowerCase(),
          })
        : "";

  const handleChange = useCallback(
    (onFieldChange: (...event: any[]) => void) => {
      return (value: string | string[] | undefined, item?: ISelectOption) => {
        if (onChange) {
          onChange(value as never, item);
        }
        onFieldChange(value);
      };
    },
    [onChange],
  );

  const selectElement = (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange: onFieldChange, value } }) => {
        const handleClear = () => {
          const clearValue = restSelectProps.multiple ? [] : undefined;
          onFieldChange(clearValue);
          if (onChange) {
            onChange(clearValue as never);
          }
        };

        return (
          <Select
            {...restSelectProps}
            onChange={handleChange(onFieldChange)}
            value={value}
            invalid={invalid || !!errorMessage}
            onClear={
              restSelectProps.onClear || (clear ? handleClear : undefined)
            }
            placeholder={placeholder}
          />
        );
      }}
    />
  );

  return (
    <FormField
      label={label}
      message={message ?? errorMessage}
      description={description}
      required={required}
      tooltipContent={tooltipContent}
      className={formElementClass}
      accessory={accessory}
      color={restSelectProps.color}
    >
      {selectElement}
    </FormField>
  );
};
